import { mergeClasses } from "@fluentui/react-components";
import { Ref, useState } from "react";
import { AssignedTagModel } from "@/common/models/tags";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import { TagStackProps } from "./types";
import { useStyle } from "./style";

/**
 * On a named line the dot is decorative — the name is right beside it and the tooltip labels the line.
 * On the colour-only line the dot is the only carrier of meaning, so it takes the accessible name and
 * its tooltip is purely visual. Either way each tag is announced exactly once.
 */
const TagDot = ({
	tag,
	decorative,
	elementRef,
}: {
	tag: AssignedTagModel;
	decorative: boolean;
	elementRef?: Ref<HTMLSpanElement>;
}) => {
	const classes = useStyle();

	return (
		<span
			ref={elementRef}
			className={classes.dot}
			style={{ backgroundColor: tag.color }}
			{...(decorative
				? { "aria-hidden": true }
				: { role: "img", "aria-label": tag.name })}
		/>
	);
};

/**
 * The dots row neither wraps nor shrinks, so it has to be bounded. Four keeps the widest case within
 * the tightest surface — the 160px header slot on an interaction card — and means an interaction with
 * five tags still renders exactly as it did when five was the hard limit.
 */
const MaxVisibleDots = 4;

/**
 * A single row of colour-only tags, each named by its own tooltip. Anything past
 * {@link MaxVisibleDots} collapses into a "+N" counter that names the remainder on hover — the
 * five-tag limit is per "Set tag" step rather than per interaction, so a flow with several such steps
 * can produce far more than fit.
 */
const TagDots = ({ tags }: { tags: AssignedTagModel[] }) => {
	const classes = useStyle();

	const visibleTags = tags.slice(0, MaxVisibleDots);
	const overflowTags = tags.slice(MaxVisibleDots);

	return (
		<div className={mergeClasses(classes.line, classes.dotsLine)}>
			{visibleTags.map((tag) => (
				<TooltipWithMount
					key={tag.id}
					relationship="inaccessible"
					content={tag.name}
					positioning="below"
					withArrow
				>
					<span className={classes.dotWrapper}>
						<TagDot tag={tag} decorative={false} />
					</span>
				</TooltipWithMount>
			))}
			{overflowTags.length > 0 && (
				<TooltipWithMount
					relationship="label"
					content={overflowTags.map((tag) => tag.name).join(", ")}
					positioning="below"
					withArrow
				>
					<span className={classes.overflow}>+{overflowTags.length}</span>
				</TooltipWithMount>
			)}
		</div>
	);
};

/**
 * A tag rendered as colour plus name. The whole line is the trigger, so hovering anywhere on the tag
 * reveals the name, but the label is aimed at the dot — the same thing the colour-only rows aim at, and
 * the only part of the line whose position does not move with the length of the name (CC4ALL-9646).
 */
const NamedTagLine = ({ tag }: { tag: AssignedTagModel }) => {
	const classes = useStyle();
	const [dotNode, setDotNode] = useState<HTMLSpanElement | null>(null);

	return (
		<TooltipWithMount
			relationship="label"
			content={tag.name}
			// Before the dot has mounted there is nothing to aim at, so fall back to the trigger. The
			// tooltip only renders on hover, by which point the ref has long since been set.
			positioning={
				dotNode
					? { position: "below", align: "center", target: dotNode }
					: "below"
			}
			withArrow
		>
			<div className={classes.line}>
				<TagDot tag={tag} decorative elementRef={setDotNode} />
				<span className={classes.name}>{tag.name}</span>
			</div>
		</TooltipWithMount>
	);
};

/**
 * Read-only rendering of the tags assigned to an interaction (CC4ALL-8822).
 *
 * The default "stacked" layout keeps as many names as fit:
 * - one tag   → colour + name
 * - two tags  → colour + name each, on their own line
 * - 3+ tags   → line one is colour + name of the first tag, line two is colour-only dots for the rest
 *
 * The "dots" layout drops every name and renders a single row of colours, for surfaces with no room
 * for text at all. Either way the full name is revealed on hover.
 */
export const TagStack = ({
	tags,
	emptyState = "dash",
	layout = "stacked",
}: TagStackProps) => {
	const classes = useStyle();

	if (!tags?.length) {
		return emptyState === "dash" ? <>-</> : null;
	}

	if (layout === "dots") {
		return <TagDots tags={tags} />;
	}

	// Two tags read better as two named lines; beyond that the names would not fit, so only the first
	// keeps its name and the remainder collapses to colours.
	const isCompact = tags.length > 2;
	const namedTags = isCompact ? tags.slice(0, 1) : tags;
	const remainingTags = isCompact ? tags.slice(1) : [];

	return (
		<div className={classes.root}>
			{namedTags.map((tag) => (
				<NamedTagLine key={tag.id} tag={tag} />
			))}
			{remainingTags.length > 0 && <TagDots tags={remainingTags} />}
		</div>
	);
};
